import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import GovernanceSection, { CommitteeList, CommitteeDetail } from '../components/governance/GovernanceSection';
import governanceApi from '../services/governanceApi.js';

const IMAGIA_API = import.meta.env.VITE_IMAGIA_API_URL || 'https://imagia.net';
const PARTNER_KEY = import.meta.env.VITE_IMAGIA_PARTNER_KEY || '';

async function fetchContributions(email, page = 1, filters = {}) {
  const params = new URLSearchParams({
    user_email: email,
    page: String(page),
    limit: '50',
  });
  if (filters.type) params.set('type', filters.type);
  if (filters.domain) params.set('domain', filters.domain);

  const res = await fetch(
    `${IMAGIA_API}/api/partner/contributions?${params}`,
    { headers: { 'X-Partner-Key': PARTNER_KEY } }
  );
  if (!res.ok) throw new Error('Failed to fetch');
  return res.json();
}

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

const TYPE_COLORS = {
  bug_report: 'bg-red-100 text-red-700',
  feature_request: 'bg-blue-100 text-blue-700',
  improvement: 'bg-green-100 text-green-700',
  feedback: 'bg-yellow-100 text-yellow-700',
  question: 'bg-purple-100 text-purple-700',
  general: 'bg-gray-100 text-gray-700',
};

const TYPE_LABELS = {
  bug_report: 'Bug Reports',
  feature_request: 'Features',
  improvement: 'Improvements',
  feedback: 'Feedback',
  question: 'Questions',
  general: 'General',
};

const DOMAIN_COLORS = {
  engineering: 'text-blue-600',
  customer_experience: 'text-green-600',
  sales: 'text-amber-600',
  integration: 'text-purple-600',
};

const DOMAIN_LABELS = {
  engineering: 'Engineering',
  customer_experience: 'Customer Experience',
  sales: 'Sales',
  integration: 'Integration',
};

const ACTION_STATUS_CONFIG = {
  agent_executing: { label: 'AI Working', color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500 animate-pulse' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
  none: { label: 'No Action', color: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
};

// ─── Breadcrumbs ───
function Breadcrumbs({ items }) {
  if (!items || items.length <= 1) return null;
  return (
    <nav className="flex items-center gap-1 text-xs text-gray-400 mb-4 overflow-x-auto">
      {items.map((crumb, i) => (
        <span key={i} className="flex items-center gap-1 whitespace-nowrap">
          {i > 0 && <ChevronRight size={12} className="text-gray-300 flex-shrink-0" />}
          {crumb.onClick ? (
            <button onClick={crumb.onClick} className="text-blue-600 hover:text-blue-800 font-medium">{crumb.label}</button>
          ) : (
            <span className="text-gray-600 font-medium">{crumb.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// ─── Task Detail ───
function TaskDetail({ itemId, onBack }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemId) return;
    setLoading(true);
    governanceApi.getItem(itemId)
      .then(d => setItem(d.item || d))
      .catch(() => setItem(null))
      .finally(() => setLoading(false));
  }, [itemId]);

  if (loading) return <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-purple-500" /></div>;
  if (!item) return <div className="p-8 text-center text-gray-400">Task not found</div>;

  const cfg = ACTION_STATUS_CONFIG[item.action_status] || ACTION_STATUS_CONFIG.none;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ChevronLeft size={16} /> Back to Tasks
      </button>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cfg.color}`}>{cfg.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                item.type === 'bug' ? 'bg-red-100 text-red-700' :
                item.type === 'feature' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>{item.type || 'task'}</span>
              {item.priority && <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                item.priority === 'high' ? 'bg-red-100 text-red-700' :
                item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                'bg-green-100 text-green-700'
              }`}>{item.priority}</span>}
            </div>
            <h2 className="text-lg font-semibold text-gray-900">{item.title}</h2>
          </div>
          <div className="text-right flex-shrink-0">
            {item.verdict_at && <p className="text-xs text-gray-400">{new Date(item.verdict_at).toLocaleDateString()}</p>}
            {item.created_at && <p className="text-[10px] text-gray-300">Created {new Date(item.created_at).toLocaleDateString()}</p>}
          </div>
        </div>

        {item.description && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{item.description}</p>
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {item.committee_name && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-400 uppercase font-medium">Committee</p>
              <p className="text-sm font-medium text-gray-900 mt-0.5">{item.committee_name}</p>
            </div>
          )}
          {item.submitted_by_name && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-400 uppercase font-medium">Submitted by</p>
              <p className="text-sm font-medium text-gray-900 mt-0.5">{item.submitted_by_name || item.submitted_by_email}</p>
            </div>
          )}
          {item.assigned_to_email && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-[10px] text-gray-400 uppercase font-medium">Assigned to</p>
              <p className="text-sm font-medium text-gray-900 mt-0.5">{item.assigned_to_email}</p>
            </div>
          )}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-[10px] text-gray-400 uppercase font-medium">Status</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5">{item.status || 'unknown'}</p>
          </div>
        </div>

        {/* Implementation steps if AI is working */}
        {item.action_steps && item.action_steps.length > 0 && (
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Implementation Steps</h4>
            <div className="space-y-1">
              {item.action_steps.map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    step.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                  }`}>{step.completed ? '✓' : i + 1}</span>
                  <span className={step.completed ? 'text-gray-500 line-through' : 'text-gray-700'}>{step.title || step.description || step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Votes */}
        {item.votes && item.votes.length > 0 && (
          <div className="mb-6">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Votes ({item.votes.length})</h4>
            <div className="flex gap-3">
              <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-green-700">{item.votes.filter(v => v.vote === 'approve').length}</p>
                <p className="text-[10px] text-green-600">Approve</p>
              </div>
              <div className="bg-red-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-red-700">{item.votes.filter(v => v.vote === 'reject').length}</p>
                <p className="text-[10px] text-red-600">Reject</p>
              </div>
              <div className="bg-yellow-50 rounded-lg px-3 py-2 text-center">
                <p className="text-lg font-bold text-yellow-700">{item.votes.filter(v => v.vote === 'needs_info').length}</p>
                <p className="text-[10px] text-yellow-600">Needs Info</p>
              </div>
            </div>
          </div>
        )}

        {/* Comments */}
        {item.comments && item.comments.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Discussion ({item.comments.length})</h4>
            <div className="space-y-3">
              {item.comments.map(c => (
                <div key={c.id} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">{c.author_name || c.author_email}</span>
                    <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-gray-600">{c.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tasks Dashboard ───
function TasksDashboard({ userEmail, onSelectTask, selectedTaskId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const headers = { 'X-Partner-Key': PARTNER_KEY, 'Content-Type': 'application/json' };
    Promise.all([
      fetch(`${IMAGIA_API}/api/partner/governance/items?user_email=${userEmail}&status=approved`, { headers }).then(r => r.json()),
      fetch(`${IMAGIA_API}/api/partner/governance/actions`, { headers }).then(r => r.json()),
    ]).then(([approved, actions]) => {
      const approvedItems = approved?.items?.items || approved?.items || [];
      const actionItems = actions?.actions || [];
      const allMap = new Map();
      for (const item of [...approvedItems, ...actionItems]) allMap.set(item.id, item);
      const order = { agent_executing: 0, pending: 1, in_progress: 2, completed: 3, none: 4 };
      const sorted = [...allMap.values()].sort((a, b) =>
        (order[a.action_status] ?? 4) - (order[b.action_status] ?? 4) ||
        new Date(b.verdict_at || b.created_at) - new Date(a.verdict_at || a.created_at)
      );
      setItems(sorted);
    }).catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [userEmail]);

  if (selectedTaskId) {
    return <TaskDetail itemId={selectedTaskId} onBack={() => onSelectTask(null)} />;
  }

  if (loading) return <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-purple-500" /></div>;

  const filtered = filter === 'all' ? items
    : filter === 'agent' ? items.filter(i => i.action_status === 'agent_executing')
    : filter === 'human' ? items.filter(i => i.action_status === 'in_progress' || (i.action_status === 'pending' && i.assigned_to))
    : filter === 'pending' ? items.filter(i => i.action_status === 'pending' && !i.assigned_to)
    : filter === 'completed' ? items.filter(i => i.action_status === 'completed')
    : items;

  const counts = {
    all: items.length,
    agent: items.filter(i => i.action_status === 'agent_executing').length,
    human: items.filter(i => i.action_status === 'in_progress' || (i.action_status === 'pending' && i.assigned_to)).length,
    pending: items.filter(i => i.action_status === 'pending' && !i.assigned_to).length,
    completed: items.filter(i => i.action_status === 'completed').length,
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {[
          { key: 'all', label: 'All Tasks', icon: '📋', color: 'border-gray-200' },
          { key: 'agent', label: 'AI Agent', icon: '⚡', color: 'border-purple-200' },
          { key: 'human', label: 'Human', icon: '👤', color: 'border-blue-200' },
          { key: 'pending', label: 'Pending', icon: '⏳', color: 'border-yellow-200' },
          { key: 'completed', label: 'Done', icon: '✅', color: 'border-green-200' },
        ].map(c => (
          <button key={c.key} onClick={() => setFilter(c.key)}
            className={`p-3 rounded-xl border-2 text-left transition-all ${filter === c.key ? c.color + ' bg-white shadow-sm' : 'border-transparent bg-gray-50 hover:bg-white'}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{c.icon}</span>
              <span className="text-2xl font-bold text-gray-900">{counts[c.key]}</span>
            </div>
            <span className="text-xs text-gray-500">{c.label}</span>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            {filter === 'all' ? 'No approved tasks yet. Items get here after committee approval.' : `No ${filter} tasks.`}
          </div>
        )}
        {filtered.map(item => {
          const cfg = ACTION_STATUS_CONFIG[item.action_status] || ACTION_STATUS_CONFIG.none;
          return (
            <button key={item.id} onClick={() => onSelectTask(item.id)}
              className="w-full bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                    <h4 className="text-sm font-medium text-gray-900 truncate">{item.title}</h4>
                  </div>
                  {item.description && <p className="text-xs text-gray-500 line-clamp-2 ml-4">{item.description}</p>}
                  <div className="flex items-center gap-2 mt-2 ml-4 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${cfg.color}`}>{cfg.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      item.type === 'bug' ? 'bg-red-100 text-red-700' :
                      item.type === 'feature' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{item.type || 'task'}</span>
                    {item.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      item.priority === 'high' ? 'bg-red-100 text-red-700' :
                      item.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>{item.priority}</span>}
                    {item.assigned_to_email && <span className="text-[10px] text-gray-400">→ {item.assigned_to_email}</span>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] text-gray-400">{item.verdict_at ? new Date(item.verdict_at).toLocaleDateString() : ''}</div>
                  {item.action_status === 'agent_executing' && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-purple-600 font-medium">
                      <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse" />
                      AI implementing
                    </div>
                  )}
                  {item.action_status === 'completed' && (
                    <div className="mt-1 text-[10px] text-green-600 font-medium">Done</div>
                  )}
                  <ChevronRight size={14} className="text-gray-300 mt-1 ml-auto" />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Page ───
export default function ContributionsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL-synced state
  const mainTab = searchParams.get('tab') || 'contributions';
  const selectedCommittee = searchParams.get('committee') || null;
  const selectedItem = searchParams.get('item') || null;
  const selectedTask = searchParams.get('task') || null;

  const navigate = useCallback((updates) => {
    setSearchParams(prev => {
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === undefined) prev.delete(k);
        else prev.set(k, v);
      }
      return prev;
    }, { replace: false }); // push to history so back button works
  }, [setSearchParams]);

  const setMainTab = (t) => navigate({ tab: t, committee: null, item: null, task: null });
  const setSelectedCommittee = (id) => navigate({ committee: id, item: null, task: null });
  const setSelectedItem = (id) => navigate({ item: id, task: null });
  const setSelectedTask = (id) => navigate({ task: id });

  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});

  useEffect(() => {
    fetch('/api/contributor/profile', { credentials: 'include' })
      .then(r => r.json())
      .then(r => r.data && setProfile(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.emailAddress) return;
    setLoading(true);
    fetchContributions(user.emailAddress, page, filters)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [user?.emailAddress, page, filters]);

  const stats = data?.stats || {};
  const ownershipPoints = stats.totalImpact || 0;
  const activeFilter = filters.type || filters.domain || null;

  function toggleFilter(key, value) {
    setPage(1);
    setFilters(prev => prev[key] === value ? {} : { [key]: value });
  }

  function clearFilter() {
    setPage(1);
    setFilters({});
  }

  // Build breadcrumbs based on current state
  const breadcrumbs = [
    { label: 'Contributions & Governance', onClick: () => setMainTab('contributions') },
  ];

  if (mainTab === 'committees') {
    breadcrumbs.push({ label: 'Committees', onClick: selectedCommittee ? () => setSelectedCommittee(null) : null });
    if (selectedCommittee) {
      breadcrumbs.push({ label: 'Committee Detail', onClick: selectedItem ? () => setSelectedItem(null) : null });
      if (selectedItem) breadcrumbs.push({ label: 'Request' });
    }
  } else if (mainTab === 'tasks') {
    breadcrumbs.push({ label: 'Tasks', onClick: selectedTask ? () => setSelectedTask(null) : null });
    if (selectedTask) breadcrumbs.push({ label: 'Task Detail' });
  } else if (mainTab === 'governance') {
    breadcrumbs.push({ label: 'Governance' });
  } else {
    breadcrumbs.push({ label: 'Contributions' });
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/account" className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
          <ChevronLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contributions & Governance</h1>
          <p className="text-sm text-gray-500">Your impact, committees, and governance decisions</p>
        </div>
      </div>

      {/* Breadcrumbs */}
      <Breadcrumbs items={breadcrumbs} />

      {/* Main tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {[
          { key: 'contributions', label: 'Contributions' },
          { key: 'committees', label: 'Committees' },
          { key: 'tasks', label: 'Tasks' },
          { key: 'governance', label: 'Governance' },
        ].map(t => (
          <button key={t.key} onClick={() => setMainTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              mainTab === t.key ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Committees Tab */}
      {mainTab === 'committees' && user?.emailAddress && (
        selectedCommittee ? (
          <CommitteeDetail committeeId={selectedCommittee} userEmail={user.emailAddress}
            selectedItemId={selectedItem} onSelectItem={setSelectedItem}
            onBack={() => setSelectedCommittee(null)} />
        ) : (
          <CommitteeList userEmail={user.emailAddress} onSelectCommittee={setSelectedCommittee} />
        )
      )}

      {/* Tasks Tab */}
      {mainTab === 'tasks' && user?.emailAddress && (
        <TasksDashboard userEmail={user.emailAddress} onSelectTask={setSelectedTask} selectedTaskId={selectedTask} />
      )}

      {/* Governance Tab */}
      {mainTab === 'governance' && user?.emailAddress && (
        <GovernanceSection userEmail={user.emailAddress} activeTab="governance" />
      )}

      {mainTab === 'contributions' && (loading && !data ? (
        <div className="flex justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Contributions" value={fmt(stats.totalContributions)} active={!activeFilter} onClick={clearFilter} />
            <StatCard label="Impact Score" value={fmt(stats.totalImpact)} active={false} onClick={null} />
            <StatCard label="Ownership Points" value={fmt(Math.round(ownershipPoints))} color="amber" active={false} onClick={null} />
            <StatCard label="Domains" value={stats.domains || 0} active={false} onClick={null} />
          </div>

          {stats.byType && Object.keys(stats.byType).length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">By Type</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                  <button key={type} onClick={() => toggleFilter('type', type)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      filters.type === type
                        ? 'ring-2 ring-offset-1 ring-blue-400 ' + (TYPE_COLORS[type] || TYPE_COLORS.general)
                        : (TYPE_COLORS[type] || TYPE_COLORS.general) + ' opacity-70 hover:opacity-100'
                    }`}>
                    {TYPE_LABELS[type] || type.replace('_', ' ')}
                    <span className="opacity-60">{fmt(count)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {stats.byDomain && Object.keys(stats.byDomain).length > 0 && (
            <div>
              <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">By Domain</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.byDomain).sort((a, b) => b[1] - a[1]).map(([domain, count]) => (
                  <button key={domain} onClick={() => toggleFilter('domain', domain)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      filters.domain === domain
                        ? 'ring-2 ring-offset-1 ring-blue-400 bg-gray-100 text-gray-800'
                        : 'bg-gray-100 text-gray-600 opacity-70 hover:opacity-100'
                    }`}>
                    {DOMAIN_LABELS[domain] || domain}
                    <span className="opacity-60">{fmt(count)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeFilter && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">
                Showing {fmt(data?.pagination?.total || 0)} {filters.type ? (TYPE_LABELS[filters.type] || filters.type) : (DOMAIN_LABELS[filters.domain] || filters.domain)} contributions
              </span>
              <button onClick={clearFilter} className="p-0.5 rounded hover:bg-gray-100 text-gray-400"><X size={14} /></button>
            </div>
          )}

          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl border border-amber-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-amber-900">Ownership</h3>
                <p className="text-sm text-amber-700 mt-1">Every contribution earns ownership points. Points convert to equity in Jubly through the Shalom Company Constitution.</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-amber-700">{fmt(Math.round(ownershipPoints))}</p>
                <p className="text-xs text-amber-500">points</p>
              </div>
            </div>
            <Link to="/equity" className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium">Learn about the equity model →</Link>
          </div>

          {profile && !profile.isContributor && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-medium text-blue-900">Your contributions are being tracked</p>
              <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                All your activity is tracked and earns contribution points. However, these points <strong>cannot be redeemed for ownership</strong> until you officially register as a contributor.
              </p>
              <button onClick={() => fetch('/api/contributor/register', { method: 'POST', credentials: 'include' }).then(() => window.location.reload())}
                className="mt-3 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700">Register as Contributor</button>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-base font-semibold text-gray-900">{activeFilter ? 'Filtered' : 'All'} Contributions ({fmt(data?.pagination?.total || 0)})</h3>
            </div>
            <div className="divide-y divide-gray-50">
              {(data?.contributions || []).length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-400">
                  {activeFilter ? 'No contributions match this filter.' : 'No contributions yet. Start by using Jubly!'}
                </div>
              ) : (data?.contributions || []).map(c => (
                <div key={c.id} className="px-6 py-3 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.title}</p>
                      {c.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{c.description}</p>}
                      <div className="flex items-center gap-2 mt-1.5">
                        <button onClick={() => toggleFilter('type', c.type)} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium cursor-pointer hover:ring-1 hover:ring-blue-300 ${TYPE_COLORS[c.type] || TYPE_COLORS.general}`}>{c.type?.replace('_', ' ')}</button>
                        <button onClick={() => toggleFilter('domain', c.domain)} className={`text-[10px] font-medium cursor-pointer hover:underline ${DOMAIN_COLORS[c.domain] || 'text-gray-500'}`}>{c.domain}</button>
                        <span className="text-[10px] text-gray-300">{new Date(c.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="text-sm font-semibold text-amber-600">+{c.impact_score || 0}</span>
                      <p className="text-[10px] text-gray-400">impact</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {data?.pagination?.total > 50 && (
              <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="text-xs text-blue-600 disabled:text-gray-300">← Previous</button>
                <span className="text-xs text-gray-400">Page {page} of {Math.ceil(data.pagination.total / 50)}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(data.pagination.total / 50)} className="text-xs text-blue-600 disabled:text-gray-300">Next →</button>
              </div>
            )}
          </div>

          {loading && data && (
            <div className="fixed inset-0 bg-white/50 flex items-center justify-center z-50 pointer-events-none">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatCard({ label, value, color = 'blue', active, onClick }) {
  const colors = { blue: 'bg-blue-50 text-blue-900', amber: 'bg-amber-50 text-amber-900' };
  const base = colors[color] || colors.blue;
  const clickable = onClick !== null;
  return (
    <div onClick={clickable ? onClick : undefined}
      className={`rounded-xl p-4 ${base} ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all' : ''} ${active ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}>
      <p className="text-[10px] font-medium uppercase tracking-wide opacity-60">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
